// apps/spa/src/hooks/useToggleReminder.ts
// Optimistic toggle for the reminder list Switch (REMIND-02).
//
// RESEARCH Pattern 8 (TanStack optimistic update) + Pitfall 5 (cancelQueries
// before optimistic write so an in-flight refetch can't clobber the cache):
//   1. onMutate cancels in-flight ['reminders'] refetches
//   2. snapshots cache for rollback
//   3. optimistically toggles `enabled` in the cached list
//   4. onError rolls back AND fires error toast
//   5. onSettled invalidates ['reminders'] so the server state wins eventually
//
// mutationFn calls PATCH /api/reminders/:id with { enabled }. Server's PATCH
// validates partial body via updateReminderSchema and writes to D1.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Reminder } from '@guardian/shared';
import { api } from '@/lib/api';

export type ToggleVars = { id: string; enabled: boolean };

export function useToggleReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: ToggleVars) =>
      api<Reminder>(`/api/reminders/${vars.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: vars.enabled }),
      }),
    onMutate: async ({ id, enabled }) => {
      await qc.cancelQueries({ queryKey: ['reminders'] });
      const previous = qc.getQueryData<Reminder[]>(['reminders']);
      qc.setQueryData<Reminder[]>(['reminders'], (old) =>
        old?.map((r) => (r.id === id ? { ...r, enabled } : r)),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['reminders'], ctx.previous);
      }
      toast.error('Could not update reminder. Try again.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['reminders'] });
    },
  });
}
